/* v88-buybox.js — makes the V88 PDP buy box actually sell.
 *
 * WHAT IT OWNS, and what it deliberately does not.
 *
 *   Owns: resolving the checked option radios to a variant, reading that
 *   variant's one-time price and its selling-plan allocation, writing the
 *   three price slots, marking impossible combinations, and posting to
 *   /cart/add.js.
 *
 *   Does not own: which chip looks selected. That is :has(input:checked) in
 *   v88.css. A script that also toggled a class would be a second source of
 *   truth for the same state, and the two drift the first time one of them
 *   is edited.
 *
 * SELF-CONTAINED PER SECTION. Everything is scoped to the .v88-pdp the data
 * script sits in, so two buy boxes on one page do not fight over ids —
 * which is why the radios carry section.id in their name attribute.
 *
 * NO BUILD STEP, ES5-ish. It ships as a raw theme asset next to the rest of
 * this theme's JS; anything newer than this would need one.
 */
(function () {
  'use strict';

  /* These lists are built as HTML strings, and a plan name is merchant
   * text — it can contain an ampersand or a quote. */
  function esc(v) {
    return String(v).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function money(cents, format) {
    var v = (cents / 100).toFixed(2).replace(/\.00$/, '');
    v = v.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    // shop.money_format is Liquid, not printf — only the amount placeholders
    // are substituted, and anything else in it is left as the shop wrote it.
    if (format && /\{\{\s*amount/.test(format)) {
      return format.replace(/\{\{\s*amount[^}]*\}\}/, v).replace(/<[^>]*>/g, '');
    }
    return '$' + v;
  }

  /* Writes a value and, when it is different from what was there, plays a
   * short lift-and-fade so the change is noticed. Guarded on the element
   * actually changing: repainting an unchanged price on every render would
   * flicker the whole column on a click that altered nothing.
   *
   * The class is removed on the next frame rather than on transitionend —
   * a reduced-motion visitor has a 1ms transition that may never fire one. */
  function setText(el, value) {
    if (!el) return;
    if (el.textContent === value) return;
    el.classList.add('v88-num-swap');
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        el.textContent = value;
        el.classList.remove('v88-num-swap');
      });
    });
  }

  function init(root) {
    var dataEl = root.querySelector('[data-v88-data]');
    if (!dataEl) return;

    var data;
    try {
      data = JSON.parse(dataEl.textContent);
    } catch (e) {
      return; // A malformed blob leaves the static markup rather than a broken box.
    }
    if (!data.variants || !data.variants.length) return;

    /* A PAGE'S SALE (2026-10-02, the Vanilla Ice lander: "50% off").
     * Every price the box can show is cut once, here, before anything reads
     * it -- the variants, their plans and the supply offers -- so the
     * subscription, the one-time card, the frequency row and the gift row
     * all quote the sale price through the code they already run, and none
     * of it needs to know there is a sale. fullOf() gives back the price
     * before it for the struck-through figures. The checkout price comes
     * from the page's discount code, not from here; this only says what
     * that code will do. 0 (every PDP) leaves the data alone. */
    var salePct = Number(data.salePct) || 0;
    if (salePct <= 0 || salePct >= 100) salePct = 0;
    /* A FIXED SALE PRICE (2026-10-06, the VIP lander: Akaash, "$178 > $89").
     * Not a percent of the list price: every unit is salePrice, struck
     * against saleWas. Both are typed in the shop's currency (AUD cents) and
     * moved into the shopper's by Shopify's rate, as the landers' other
     * fixed figures are (fx-conversion). It wins over salePct. Like the
     * percent, it only says what the checkout will do; a discount has to
     * make it true. */
    var fxRate = (window.Shopify && Shopify.currency && parseFloat(Shopify.currency.rate)) || 1;
    var salePrice = Math.round((Number(data.salePrice) || 0) * fxRate);
    var saleWas = Math.round((Number(data.saleWas) || 0) * fxRate);
    if (salePrice > 0) salePct = 0;
    var onSale = salePct > 0 || (salePrice > 0 && saleWas > salePrice);
    function cut(c) {
      if (c === null || c === undefined) return c;
      if (salePrice > 0) return salePrice;
      return Math.round(c * (100 - salePct) / 100);
    }
    function cutPlans(plans) { Object.keys(plans || {}).forEach(function (k) { plans[k] = cut(plans[k]); }); }
    function fullOf(c) {
      if (salePrice > 0) return saleWas > salePrice ? Math.round(c * saleWas / salePrice) : c;
      return salePct ? Math.round(c * 100 / (100 - salePct)) : c;
    }
    if (salePct || salePrice > 0) {
      data.variants.forEach(function (v) { v.price = cut(v.price); cutPlans(v.plans); });
      (data.offers || []).forEach(function (o) {
        Object.keys(o.bySku || {}).forEach(function (s) {
          var r = o.bySku[s];
          r.price = cut(r.price);
          r.planPrice = cut(r.planPrice);
          cutPlans(r.plans);
        });
      });
    }

    var els = {
      chosen: root.querySelectorAll('[data-v88-chosen]'),
      optInputs: root.querySelectorAll('input[data-v88-opt]'),
      modeInputs: root.querySelectorAll('input[data-v88-mode]'),
      freq: root.querySelector('[data-v88-freq]'),
      freqList: root.querySelector('[data-v88-freq-list]'),
      freqNote: root.querySelector('[data-v88-freq-note]'),
      freqTitle: root.querySelector('[data-v88-freq-title]'),
      freqTick: root.querySelector('[data-v88-freq-tick]'),
      planName: root.querySelector('[data-v88-plan-name]'),
      subQty: root.querySelector('[data-v88-subqty]'),
      subPrice: root.querySelector('[data-v88-sub-price]'),
      oncePrice: root.querySelector('[data-v88-once-price]'),
      total: root.querySelector('[data-v88-total]'),
      totalWas: root.querySelector('[data-v88-total-was]'),
      perServe: root.querySelector('[data-v88-perserve]'),
      subWas: root.querySelector('[data-v88-sub-was]'),
      onceWas: root.querySelector('[data-v88-once-was]'),
      totalUnit: root.querySelector('[data-v88-total-unit]'),
      save: root.querySelector('[data-v88-save]'),
      cta: root.querySelector('[data-v88-cta]'),
      error: root.querySelector('[data-v88-error]'),
      banner: root.querySelector('[data-v88-sub-banner]'),
      subCard: root.querySelector('.v88-plan--sub'),
      onceCard: root.querySelector('.v88-plan--once'),
      qty: root.querySelector('[data-v88-qty]')
    };
    if (!els.cta) return;

    /* A CAPPED ONE-TIME QUANTITY (2026-10-06, the VIP lander: "max 3 per
     * person"). maxQty > 0 shows the stepper (v88-buybox.liquid, max_qty),
     * the one-time total follows it, and the add checks the cart first so
     * the line never goes past the cap. The line carries _max_qty so the
     * cart drawer holds it there too. 0 everywhere else: quantity 1, as before. */
    var maxQty = Math.max(0, parseInt(data.maxQty, 10) || 0);
    /* qtySoft (2026-10-06, Akaash: "dont actually make it 3 per person, we're
     * just saying it so people try and order more"): the picker and its note
     * stay, but the add doesn't check the cart and the line carries no cap. */
    var qtySoft = !!data.qtySoft;
    /* qtySub (2026-10-15, Akaash: "Add quantity selector so people can
     * increase the amount they purchase on PDP every X amount of days"): the
     * same picker sets the units on each delivery. subMult, worked out in
     * render(), is that count while subscribing and 1 otherwise. */
    var qtySub = !!data.qtySub && maxQty > 0;
    var buyQty = 1;
    function paintQty() {
      if (!els.qty) return;
      var val = els.qty.querySelector('[data-v88-qty-val]');
      if (val) val.textContent = buyQty;
      /* The try-once card's box (once_qty): "1 Jar (30 Serves)", "2 Jars (60 Serves)". */
      var txt = els.qty.querySelector('[data-v88-qty-text]');
      if (txt) {
        var per = parseInt(txt.getAttribute('data-v88-qty-serves'), 10) || 0;
        txt.textContent = (txt.getAttribute('data-v88-qty-tpl') || '[n]')
          .replace(/\[n\]/g, buyQty)
          .replace(/\[s\]/g, buyQty === 1 ? '' : 's')
          .replace(/\[serves\]/g, per * buyQty);
      }
      els.qty.querySelectorAll('[data-v88-qty-step]').forEach(function (b) {
        var step = Number(b.getAttribute('data-v88-qty-step'));
        b.disabled = step < 0 ? buyQty <= 1 : buyQty >= maxQty;
      });
    }
    if (els.qty && maxQty > 0) {
      els.qty.addEventListener('click', function (e) {
        var b = e.target.closest ? e.target.closest('[data-v88-qty-step]') : null;
        if (!b || b.disabled) return;
        buyQty = Math.min(maxQty, Math.max(1, buyQty + Number(b.getAttribute('data-v88-qty-step'))));
        if (els.error) els.error.hidden = true;
        paintQty();
        render();
      });
      paintQty();
    }
    /* The buy row's dropdown (qty_sub, 2026-10-15, Emily's "1 v" box): the
       same count as the stepper, chosen from a list. */
    var qtySelect = root.querySelector('[data-v88-qty-select]');
    if (qtySelect && maxQty > 0) {
      qtySelect.addEventListener('change', function () {
        buyQty = Math.min(maxQty, Math.max(1, parseInt(qtySelect.value, 10) || 1));
        if (els.error) els.error.hidden = true;
        render();
      });
    }

    var ctaLabel = els.cta.textContent;
    /* A waitlist button (the TwoStrong template) opens the Launching Soon
       popup — fon-inline-klaviyo-oos.js is listening on it — so it stays enabled and
       keeps its label whatever the stock says, and never adds to the cart.
       The plans and prices around it still follow the choices. */
    var waitlist = els.cta.hasAttribute('data-v88-waitlist');
    var bannerTpl = els.banner ? (els.banner.getAttribute('data-v88-banner-tpl') || els.banner.textContent) : '';
    /* [annual] per offer (sections/v88-buybox.liquid, annual_saving): "*" is
       the default, other keys are offer handles (OneLife's For Two). */
    var annualMap = null;
    if (els.banner && els.banner.getAttribute('data-v88-annual')) {
      annualMap = {};
      els.banner.getAttribute('data-v88-annual').split(';;').forEach(function (pair) {
        var bits = pair.split('|');
        if (bits.length === 2) annualMap[bits[0].trim()] = bits[1].trim();
      });
    }
    var freqTitle = els.freqTitle ? els.freqTitle.textContent : '';

    /* COMING SOON (V4.7). An unavailable variant with a Klaviyo form of its
       own is not sold out but not out yet: the button stays live and opens
       that form. The modal moves to <body> so no transformed or clipped
       ancestor traps it. Absent on V4.6 and TwoStrong, where soon() is
       always false. */
    var servesEls = root.querySelectorAll('[data-v88-serves]');
    /* The server writes the plain line as the base where a perk has a pouch
       line too (data-v88-serves-pouch), since it may have drawn the pouch's. */
    Array.prototype.forEach.call(servesEls, function (el) { if (!el.hasAttribute('data-v88-serves-base')) el.setAttribute('data-v88-serves-base', el.textContent); });
    var notifyEl = root.querySelector('[data-v88-notify]');
    if (notifyEl) document.body.appendChild(notifyEl);
    var stockEl = root.querySelector('.v88-pdp__stock');
    var stockHidden = stockEl ? stockEl.hidden : false;
    var notifyBack = null;

    function soon(v) {
      return !!(v && !v.available && v.notify && notifyEl && /^[\w-]+$/.test(v.notify) &&
        notifyEl.querySelector('[data-v88-notify-form="' + v.notify + '"]'));
    }

    /* A product that is Coming Soon as a whole (soon_on — V4.7, Renz: "not
       sold out, coming soon") says so for a pick with no form yet too:
       a plain Coming Soon, not Sold Out. */
    function waiting(v) {
      return !!(v && !v.available && data.soonAll);
    }

    /* "Tropical Crush · 28-Sachet Box": flavour, then the format title-cased
       from the variant's own value (the trial's box has no chip to read). */
    function notifyName(v) {
      var fmt = String(v.opts[0] || '').replace(/(^|[\s-])([a-z])/g, function (m, p, c) {
        return p + c.toUpperCase();
      });
      return [v.opts[1], fmt].filter(Boolean).join(' · ');
    }

    function openNotify(v) {
      Array.prototype.forEach.call(notifyEl.querySelectorAll('[data-v88-notify-form]'), function (el) {
        el.hidden = el.getAttribute('data-v88-notify-form') !== v.notify;
      });
      var name = notifyEl.querySelector('[data-v88-notify-name]');
      if (name) name.textContent = notifyName(v);
      /* layout/theme.liquid defers Klaviyo to the first scroll or touch; a
         click can come first. Same guard as v88-twostrong-reserve.js. */
      if (!document.querySelector('script[src^="https://static.klaviyo.com/onsite/js/"]')) {
        var k = document.createElement('script');
        k.src = 'https://static.klaviyo.com/onsite/js/YnKpzp/klaviyo.js?company_id=YnKpzp';
        k.async = true;
        document.head.appendChild(k);
      }
      notifyBack = document.activeElement;
      notifyEl.hidden = false;
      document.documentElement.classList.add('v88-notify-open');
      var x = notifyEl.querySelector('button[data-v88-notify-close]');
      if (x) x.focus();
    }

    function closeNotify() {
      notifyEl.hidden = true;
      document.documentElement.classList.remove('v88-notify-open');
      if (notifyBack && notifyBack.focus) notifyBack.focus();
      notifyBack = null;
    }

    /* The page's own picture in the form (notify_image, Nov 3, Emily: "pop up
       image missed - let's just use the same image that we're using for
       onelife faq"). The picture belongs to the Klaviyo form, which other
       pages share, so it is swapped here: every image the form draws in this
       dialog, its later steps included, shows this one. */
    var notifyImg = notifyEl ? notifyEl.getAttribute('data-v88-notify-img') : '';
    if (notifyImg && window.MutationObserver) {
      /* A cap on the writes, so a form that sets its picture back can never
         trade writes with this forever: past it, the form keeps its own. */
      var notifySwaps = 0;
      var notifyObs;
      var swapNotifyImg = function () {
        if (notifySwaps > 40) { if (notifyObs) notifyObs.disconnect(); return; }
        Array.prototype.forEach.call(notifyEl.querySelectorAll('[data-v88-notify-form] picture source'), function (s) {
          if (s.getAttribute('srcset') !== notifyImg) { s.setAttribute('srcset', notifyImg); notifySwaps++; }
        });
        Array.prototype.forEach.call(notifyEl.querySelectorAll('[data-v88-notify-form] img'), function (im) {
          if (im.hasAttribute('srcset')) { im.removeAttribute('srcset'); notifySwaps++; }
          if (im.getAttribute('src') !== notifyImg) { im.setAttribute('src', notifyImg); notifySwaps++; }
        });
      };
      notifyObs = new MutationObserver(swapNotifyImg);
      notifyObs.observe(notifyEl, { childList: true, subtree: true, attributes: true, attributeFilter: ['src', 'srcset'] });
      swapNotifyImg();
    }

    if (notifyEl) {
      notifyEl.addEventListener('click', function (e) {
        if (e.target.closest && e.target.closest('[data-v88-notify-close]')) closeNotify();
      });
      document.addEventListener('keydown', function (e) {
        if ((e.key === 'Escape' || e.key === 'Esc') && !notifyEl.hidden) closeNotify();
      });
    }

    /* The CTA's soon state, and the "In Stock" line that would contradict it. */
    function setSoon(on, plain) {
      els.cta.classList.toggle('is-soon', on);
      els.cta.classList.toggle('is-waiting', !!plain);
      if (stockEl) stockEl.hidden = on || !!plain || stockHidden;
    }

    function chosenOptions() {
      var out = [];
      Array.prototype.forEach.call(els.optInputs, function (input) {
        if (input.checked) out[Number(input.getAttribute('data-v88-opt'))] = input.value;
      });
      return out;
    }

    function findVariant(opts) {
      for (var i = 0; i < data.variants.length; i++) {
        var v = data.variants[i];
        var hit = true;
        for (var j = 0; j < opts.length; j++) {
          if (opts[j] !== undefined && v.opts[j] !== opts[j]) { hit = false; break; }
        }
        if (hit) return v;
      }
      return null;
    }

    /* THE TRIAL'S OWN FORMAT (V4.7, Sep 29). A product whose trial is a
     * format of its own (the 14-sachet box) leaves it out of the format
     * choice; the one-time card sells it in the chosen flavour while the
     * subscription sells the chosen format. Null on every other product. */
    var trialFmt = data.trialFormat || '';
    function withTrial(opts) { var o = opts.slice(); o[0] = trialFmt; return o; }
    function trialVariant(opts) { return trialFmt ? findVariant(withTrial(opts)) : null; }

    /* Shopify's plan names read "Ships every 30-days" (V4.7); the page's own
       say "Delivered Every 30 Days", so they are shown that way (Sep 29,
       "like the OneFit PDP"). Any other name is shown as it is. */
    function prettyPlan(name) {
      var m = /^ships every (\d+)[\s-]*days?$/i.exec(String(name || '').trim());
      return m ? 'Delivered Every ' + m[1] + ' Days' : name;
    }

    /* How many days a plan's own name says it ships in. Weeks and months are
     * both written out on this shop ("Ships every 6 weeks", "Ships every
     * 30-days"), so both are read; anything unparseable returns 0 and the
     * caller keeps the supply figure it already had. */
    function planDays(id) {
      var raw = '';
      for (var i = 0; i < (data.plans || []).length; i++) {
        if (String(data.plans[i].id) === String(id)) { raw = data.plans[i].name || ''; break; }
      }
      var m = /(\d+)\s*-?\s*(day|week|month)/i.exec(raw);
      if (!m) return 0;
      var n = parseInt(m[1], 10);
      var unit = m[2].toLowerCase();
      if (unit === 'week') return n * 7;
      if (unit === 'month') return n * 30;
      return n;
    }

    /* "4 Weeks" from "Ships every 4 weeks", so the billing line can say the
     * same cycle the title does. Empty for a plan named in days, which the
     * days template already reads. */
    function planEvery(id) {
      var raw = '';
      for (var i = 0; i < (data.plans || []).length; i++) {
        if (String(data.plans[i].id) === String(id)) { raw = data.plans[i].name || ''; break; }
      }
      var m = /(\d+)\s*-?\s*(week|month)s?/i.exec(raw);
      if (!m) return '';
      var n = parseInt(m[1], 10);
      var unit = m[2].toLowerCase() === 'week' ? 'Week' : 'Month';
      return n + ' ' + unit + (n === 1 ? '' : 's');
    }

    function planName(id) {
      for (var i = 0; i < (data.plans || []).length; i++) {
        if (String(data.plans[i].id) === String(id)) return prettyPlan(data.plans[i].name);
      }
      return '';
    }

    /* A plan belongs to a variant, not to the product — OneFit carries two
     * plans of the same name, one per packaging — so the frequency options
     * are rebuilt from whichever variant is selected.
     *
     * The chosen plan is remembered by NAME rather than by id, because the
     * id changes when the packaging does while the cadence the shopper
     * picked does not. */
    var chosenPlan = '';

    /* THE CADENCE THE PAGE OPENS ON (2026-10-27, Akaash: "for the site PDP
     * for pouch subscriptions can you default this to '8 Weeks'"). The
     * section's default_freq names it ("8 weeks"); a page that sets none, or
     * a product with no such plan, opens on the first plan as before. Only
     * the opener: once the shopper picks, chosenPlan keeps their pick. */
    function openingPlan(list) {
      var want = String(data.defaultFreq || '').trim().toLowerCase();
      if (want) {
        for (var i = 0; i < list.length; i++) {
          if ((' ' + list[i].name.toLowerCase()).indexOf(' ' + want) !== -1) return list[i];
        }
      }
      return list[0];
    }

    /* SUPPLY OFFERS take over the same control when the section is given
     * any. The artboard's dropdown picks a supply PERIOD — 30 / 60 / 90 day
     * — and those are separate products bought at a quantity, not cadences
     * of one plan. Where they exist they win, because a page offering both
     * a period and a cadence in one select would be offering two different
     * questions in one control.
     *
     * Everything is matched to the chosen flavour by SKU: the supply
     * products carry the base product's SKUs, so one flavour choice picks
     * the right variant in all of them. */
    var offers = (data.offers || []).filter(function (o) { return o && o.bySku; });
    var hasOffers = offers.length > 0;
    /* WHICH TIER THE PAGE OPENS ON.
     *
     * Where the offers are supply PERIODS (OneFit's 30 / 60 / 90-day), the
     * biggest is the opener: it is the best value and the only one carrying
     * the gifts, and opening on the 30-day described the smallest order
     * anyone could place.
     *
     * Where they are a household size (OneLife AUS's For One / For Two,
     * 2026-10-01, on request) the first row opens instead. Buying for two is
     * a fact about the reader, not an upgrade, and a page that assumes it
     * puts a jar they did not ask for in the cart. A row carrying its own
     * supply label keeps the old rule; the setting's order decides the rest. */
    /* The unit the card already quotes ("/mo"), taken from the markup so the
     * chips say the same thing as the price above them. */
    var subUnitEl = root.querySelector('.v88-plan__price small');
    // A blank unit is a choice (OneLife AUS), not a missing one.
    var baseUnit = subUnitEl ? subUnitEl.textContent.trim() : '/mo';
    var subUnit = baseUnit;
    /* "/mo" only reads true where a delivery covers a month. An offer whose
     * plans ship in weeks (OneLife AUS, OneFit V4.7's subscription) carries
     * no unit at all, whatever the template's own unit says -- one page can
     * carry both kinds, V4.6's 30/60/90-day and V4.7's 4/6/8-week, and each
     * has to say what it is. It said "/delivery" until Emily, 2026-10-01:
     * "remove 'delivery' and leave $99 -- it's now very clear that it's a
     * subscription", and the word crowded the price on the phone. The row
     * beneath ("Billed Every 4 Weeks") still names the cycle. */
    function unitForOffer(o, variant) {
      var row = o && offerRow(o, variant);
      if (!row || !row.plans) return baseUnit;
      var ids = Object.keys(row.plans);
      for (var u = 0; u < ids.length; u++) if (planEvery(ids[u])) return '';
      return baseUnit;
    }
    var chosenOffer = null;
    /* The cadence id the frequency row is on, when the offers carry plans. */
    var chosenPlanId = null;
    if (hasOffers) {
      /* "90-Day Supply", "Delivered Every 60 Days" — a period names days or
       * a supply; "For Two" does not. */
      var periodOffers = offers.some(function (o) { return /day|supply|month|week/i.test(o.label || ''); });
      chosenOffer = periodOffers
        ? offers.reduce(function (best, o) {
            return (o.qty || 1) > (best.qty || 1) ? o : best;
          }, offers[0]).key
        : offers[0].key;
    }

    function skuOf(variant) {
      return variant ? (data.skuByVariant || {})[String(variant.id)] : null;
    }

    /* The offer row for this flavour, or null when the supply product has no
     * variant with that SKU. */
    function offerRow(offer, variant) {
      var sku = skuOf(variant);
      return (sku && offer.bySku[sku]) ? offer.bySku[sku] : null;
    }

    function currentOffer() {
      for (var i = 0; i < offers.length; i++) if (offers[i].key === chosenOffer) return offers[i];
      return offers[0] || null;
    }

    /* What one delivery of this offer costs: the plan price when the supply
     * product has a plan, its list price otherwise, times the quantity that
     * makes up the period. */
    function offerBilled(offer, variant) {
      var row = offerRow(offer, variant);
      if (!row) return null;
      /* The chosen cadence's price when the row carries one -- a four-week
       * ship and an eight-week ship need not cost the same -- and the first
       * allocation, then the list price, when it does not. */
      var unit = null;
      if (chosenPlanId && row.plans && row.plans[chosenPlanId] !== undefined) {
        unit = row.plans[chosenPlanId];
      } else if (row.planPrice !== null && row.planPrice !== undefined) {
        unit = row.planPrice;
      } else {
        unit = row.price;
      }
      return unit * (offer.qty || 1);
    }

    function syncFreq(variant) {
      if (!els.freq) return null;

      if (hasOffers) {
        var usable = offers.filter(function (o) { var r = offerRow(o, variant); return r && r.available; });
        /* OUT OF STOCK STILL QUOTES A PRICE (2026-10-01, on request).
         * Hiding the panel took the labels and their figures with it, so a
         * sold-out flavour showed a plan with no price against it. Fall back
         * to every offer that has a row for this variant: the reader sees
         * what it costs, and the button keeps saying sold out. Only when no
         * offer knows this variant at all does the panel go. */
        if (!usable.length) {
          usable = offers.filter(function (o) { return offerRow(o, variant); });
        }
        if (!usable.length) {
          els.freq.hidden = true;
          if (els.freqTick) els.freqTick.hidden = true;
          if (els.subQty) els.subQty.hidden = true;
          return null;
        }
        els.freq.hidden = false;

        /* SUPPLY PERIODS STAY IN THE ROW (2026-10-01, a regression). Where the
         * offers are periods -- OneFit V4.6's Delivered Every 30 / 60 / 90
         * Days, each its own supply product -- the frequency row IS the
         * choice, as it always was: no chips, no heads, the period and what
         * it bills. Only offers that are a household size (For One / For
         * Two) become chips over a cadence row. Judged on what this flavour
         * can actually buy, so V4.7's single weekly subscription, sitting in
         * the same template as V4.6's periods, still gets its cadence row. */
        var usablePeriod = usable.some(function (o) { return /day|supply|month/i.test(o.label || ''); });
        if (usablePeriod) {
          if (els.subQty) { els.subQty.innerHTML = ''; els.subQty.hidden = true; }
          chosenPlanId = null;
          var pickO = usable[0];
          for (var k = 0; k < usable.length; k++) if (usable[k].key === chosenOffer) pickO = usable[k];
          chosenOffer = pickO.key;
          var onlyOne = usable.length === 1;
          els.freq.classList.toggle('is-static', onlyOne);
          if (onlyOne) els.freq.open = false;
          if (els.freqTick) els.freqTick.hidden = !onlyOne;
          if (els.freqTitle) els.freqTitle.textContent = pickO.label;
          if (els.freqList) {
            var oh = '';
            /* The period alone, no price beside it (2026-10-02, on request:
               "remove prices in this drop down"), as V4.7's list already
               reads. What the period bills is still on the row's note. */
            for (var j2 = 0; j2 < usable.length; j2++) {
              var o = usable[j2];
              oh += '<li><button type="button" data-v88-freq-opt="' + esc(o.key) + '" data-v88-freq-offer="1"' +
                    (o.key === pickO.key ? ' aria-current="true"' : '') + '>' +
                    '<span>' + esc(o.label) + '</span>' +
                    '</button></li>';
            }
            els.freqList.innerHTML = oh;
          }
          return pickO.key;
        }

        /* HOW MANY, THEN HOW OFTEN (2026-10-01).
         * The offers are the quantity now -- For One, For Two -- so they are
         * drawn as chips above, and this row goes back to the cadence, read
         * off whichever supply product the chosen chip names. */
        var pickQ = usable[0];
        for (var q = 0; q < usable.length; q++) if (usable[q].key === chosenOffer) pickQ = usable[q];
        chosenOffer = pickQ.key;

        if (els.subQty) {
          if (usable.length > 1) {
            /* A head per jar, as the 2026 PDP's picker did: one silhouette on
             * For One, two on For Two, so the choice reads before the words
             * do. Inline, because an asset would be a request per chip for
             * 14px of glyph. */
            var head = '<svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true" focusable="false">' +
                       '<circle cx="8" cy="5" r="3.1"/>' +
                       '<path d="M1.9 15c0-3.1 2.7-5.2 6.1-5.2s6.1 2.1 6.1 5.2z"/></svg>';
            /* What one jar costs on each tier, set against the TRIAL -- one
             * jar bought once (2026-10-01, on request). Against the single
             * tier, For One saved nothing by definition and showed no figure;
             * against the trial both tiers say what subscribing saves:
             * $129 vs $149 is 13%, $104.50 a jar vs $149 is 30%. Per jar,
             * because that is the only figure the tiers share. A page with no
             * trial format measures against the variant bought once, which on
             * OneLife AUS is the same $149 jar. */
            var perJar = function (o) {
              var b = offerBilled(o, variant);
              return b === null ? null : b / Math.max(1, o.qty || 1);
            };
            var trialRef = trialFmt ? trialVariant(variant.opts) : null;
            var basePer = trialRef ? trialRef.price : variant.price;

            var qh = '';
            for (var qi = 0; qi < usable.length; qi++) {
              var oq = usable[qi];
              var qrow = offerRow(oq, variant);
              var qbilled = offerBilled(oq, variant);
              var mine = perJar(oq);
              var saves = (basePer && mine && mine < basePer)
                ? Math.round((1 - mine / basePer) * 100) : 0;
              var heads = '';
              for (var hq = 0; hq < Math.max(1, oq.qty || 1); hq++) heads += head;
              qh += '<button type="button" class="v88-plan__qty-opt" data-v88-subqty-opt="' + esc(oq.key) + '"' +
                    (oq.key === pickQ.key ? ' aria-current="true"' : '') +
                    /* One jar's price has no room on a single line, so it
                     * rides on the title. */
                    (mine !== null && (oq.qty || 1) >= 2
                      ? ' title="' + esc(money(mine, data.moneyFormat)) + ' a jar"' : '') +
                    (qrow && qrow.available ? '' : ' data-v88-oos="1"') + '>' +
                    /* Built like the cadence row it sits above (2026-10-01, on
                     * request): a title line and a small line under it, in the
                     * same fonts, so the two controls read as one family. */
                    '<span class="v88-plan__qty-ico">' + heads + '</span>' +
                    '<span class="v88-plan__qty-text">' +
                      '<strong class="v88-plan__qty-name">' + esc(oq.label) + '</strong>' +
                      /* Amount, unit and saving in their own spans, so a phone can
                       * drop the unit (the plan price above already says it) and
                       * put the saving on its own line. */
                      (qbilled === null ? '' : '<small class="v88-plan__qty-price">' +
                        '<span class="v88-plan__qty-amt">' + esc(money(qbilled, data.moneyFormat)) + '</span>' +
                        (unitForOffer(oq, variant) ? '<span class="v88-plan__qty-unit"> ' + esc(unitForOffer(oq, variant)) + '</span>' : '') +
                        (saves > 0 ? ' <span class="v88-plan__qty-save">Save ' + saves + '%</span>' : '') +
                      '</small>') +
                    '</span>' +
                    '</button>';
            }
            els.subQty.innerHTML = qh;
            els.subQty.hidden = false;
          } else {
            els.subQty.innerHTML = '';
            els.subQty.hidden = true;
          }
        }

        var qrowSel = offerRow(pickQ, variant) || {};
        var oseen = {}, oopts = [];
        Object.keys(qrowSel.plans || {}).forEach(function (id) {
          var nm = planName(id) || 'Subscription';
          if (!oseen[nm]) { oseen[nm] = true; oopts.push({ id: id, name: nm }); }
        });

        /* No cadence on the supply product: the row states the offer itself,
         * as it did before the split. */
        if (!oopts.length) {
          els.freq.classList.add('is-static');
          els.freq.open = false;
          if (els.freqTick) els.freqTick.hidden = false;
          if (els.freqTitle) els.freqTitle.textContent = pickQ.label;
          if (els.freqList) els.freqList.innerHTML = '';
          chosenPlanId = null;
          return pickQ.key;
        }

        var opick = openingPlan(oopts);
        for (var oi = 0; oi < oopts.length; oi++) if (oopts[oi].name === chosenPlan) opick = oopts[oi];
        chosenPlan = opick.name;
        chosenPlanId = opick.id;

        var osingle = oopts.length === 1;
        els.freq.classList.toggle('is-static', osingle);
        if (osingle) els.freq.open = false;
        if (els.freqTick) els.freqTick.hidden = !osingle;
        if (els.freqTitle) els.freqTitle.textContent = opick.name;

        if (els.freqList) {
          var ohh = '';
          for (var oj = 0; oj < oopts.length; oj++) {
            /* Each cadence with what it bills, as the closed row reads. */
            var ojPrice = (data.freqPrice !== false && qrowSel.plans && qrowSel.plans[oopts[oj].id] !== undefined)
              ? qrowSel.plans[oopts[oj].id] * (pickQ.qty || 1) : null;
            ohh += '<li><button type="button" data-v88-freq-opt="' + esc(oopts[oj].id) + '"' +
                   (oopts[oj].id === opick.id ? ' aria-current="true"' : '') + '>' +
                   '<span>' + esc(oopts[oj].name) + '</span>' +
                   (ojPrice === null ? '' : '<em>' + esc(money(ojPrice, data.moneyFormat)) + '</em>') +
                   '</button></li>';
          }
          els.freqList.innerHTML = ohh;
        }
        return pickQ.key;
      }


      var seen = {};
      var opts = [];
      Object.keys(variant.plans || {}).forEach(function (id) {
        var name = planName(id) || 'Subscription';
        if (!seen[name]) { seen[name] = true; opts.push({ id: id, name: name }); }
      });

      if (!opts.length) {
        els.freq.hidden = true;
        if (els.freqTick) els.freqTick.hidden = true;
        return null;
      }

      var pick = openingPlan(opts);
      for (var i = 0; i < opts.length; i++) { if (opts[i].name === chosenPlan) pick = opts[i]; }
      chosenPlan = pick.name;

      var single = opts.length === 1;
      els.freq.classList.toggle('is-static', single);
      if (single) els.freq.open = false;
      if (els.freqTick) els.freqTick.hidden = !single;
      if (els.freqTitle) els.freqTitle.textContent = pick.name;

      if (els.freqList) {
        var html = '';
        for (var j = 0; j < opts.length; j++) {
          html += '<li><button type="button" data-v88-freq-opt="' + esc(opts[j].id) + '"' +
                  (opts[j].id === pick.id ? ' aria-current="true"' : '') + '>' +
                  '<span>' + esc(opts[j].name) + '</span></button></li>';
        }
        els.freqList.innerHTML = html;
      }
      return pick.id;
    }

    function mode() {
      for (var i = 0; i < els.modeInputs.length; i++) {
        if (els.modeInputs[i].checked) return els.modeInputs[i].value;
      }
      return 'sub';
    }

    /* A chip whose value cannot be combined with the rest of the selection
     * is dimmed rather than removed: removing it makes the row reflow and
     * hides that the flavour exists at all. */
    function markUnavailable(opts) {
      Array.prototype.forEach.call(els.optInputs, function (input) {
        var pos = Number(input.getAttribute('data-v88-opt'));
        var probe = opts.slice();
        probe[pos] = input.value;
        var v = findVariant(probe);
        var dead = !v || !v.available;
        input.parentNode.classList.toggle('is-out', dead);
        /* The stock pill (live PR #95): In Stock / Sold Out for a flavour
         * this format comes in, nothing for one it does not. */
        var pill = input.parentNode.querySelector('[data-v88-pill]');
        if (pill) {
          var ahead = soon(v) || waiting(v);
          pill.hidden = !v;
          pill.textContent = ahead ? (soon(v) ? (data.notifyLabel || 'Coming Soon') : 'Coming Soon') : (v && v.available ? 'In Stock' : 'Sold Out');
          pill.classList.toggle('is-in', !!(v && v.available));
          pill.classList.toggle('is-soon', ahead);
        }
      });
      /* The collapsed row greys with its pick, as the live selected tile does. */
      var chosen = findVariant(opts);
      Array.prototype.forEach.call(root.querySelectorAll('[data-v88-drop-picked]'), function (el) {
        el.classList.toggle('is-out', !chosen || !chosen.available);
      });
    }

    function render() {
      var opts = chosenOptions();
      var variant = findVariant(opts);
      var isSub = mode() === 'sub';

      /* Published for the bundle section below, which needs to follow the
       * same flavour. One attribute in one direction is the whole contract
       * between the two sections. */
      if (variant) root.setAttribute('data-v88-sku', skuOf(variant) || '');

      Array.prototype.forEach.call(els.chosen, function (slot) {
        var pos = Number(slot.getAttribute('data-v88-chosen'));
        /* The chip's own display name where it has one (V4.7's "28-sachet
           box" reads Sachets, as on its box), else the value. */
        var on = root.querySelector('input[data-v88-opt="' + pos + '"]:checked');
        var shown = on && on.getAttribute('data-v88-label');
        if (opts[pos]) slot.textContent = shown || opts[pos];
      });

      /* The collapsed flavour row shows what is chosen while it is shut,
       * so the button carries the same word the label above it does. */
      Array.prototype.forEach.call(root.querySelectorAll('[data-v88-drop]'), function (drop) {
        var pos = Number(drop.getAttribute('data-v88-choice'));
        var now = drop.querySelector('[data-v88-drop-current]');
        var picked = drop.querySelector('input[data-v88-opt]:checked');
        if (now && opts[pos]) {
          /* The checked chip already holds the full and the short name
           * (the phone shows the short one); the box copies the pair, so
           * the two are worked out in one place — the Liquid. */
          var fullEl = picked && picked.parentNode ? picked.parentNode.querySelector('.v88-choice__full') : null;
          var shortEl = picked && picked.parentNode ? picked.parentNode.querySelector('.v88-choice__short') : null;
          now.textContent = '';
          if (fullEl && shortEl) {
            now.appendChild(fullEl.cloneNode(true));
            now.appendChild(shortEl.cloneNode(true));
          } else {
            now.textContent = opts[pos];
          }
        }
        /* The dot follows the name. The chip that is checked already
         * carries the right colour, so it is copied rather than the
         * variant being looked up a second time. */
        var dot = drop.querySelector('[data-v88-drop-swatch]');
        var from = picked && picked.parentNode ? picked.parentNode.querySelector('.v88-choice__swatch') : null;
        if (dot && from) dot.style.background = from.style.background;
      });

      /* On the trial the stock pills speak for the trial's box. */
      markUnavailable(!isSub && trialFmt ? withTrial(opts) : opts);

      if (els.planName) {
        /* The card's own words, before its <small>. The try-once card wraps
           its two names in spans (once_title), so read the one it shows. */
        var cardName = function (card) {
          var n = card.querySelector('.v88-plan__name-picked') || card.querySelector('.v88-plan__name-rest') || card.querySelector('.v88-plan__name');
          return n && n.firstChild ? (n.firstChild.textContent || '').trim() : '';
        };
        els.planName.textContent = isSub
          ? cardName(els.subCard) || 'Subscription'
          : (els.onceCard ? cardName(els.onceCard) || 'One-time' : 'One-time');
      }

      if (!variant) {
        if (!waitlist) {
          els.cta.disabled = true;
          els.cta.textContent = 'Unavailable';
          setSoon(false);
        }
        if (els.total) els.total.textContent = '';
        if (els.totalWas) els.totalWas.hidden = true;
        if (els.totalUnit) els.totalUnit.hidden = true;
        if (els.save) els.save.hidden = true;
        return;
      }

      var pid = syncFreq(variant);
      var once = variant.price;
      /* The one-time card's own figure: the trial box where the product has
         one, else this variant. `once` stays the chosen format's list price,
         which the first-order row and the savings are measured against. */
      var trialV = trialVariant(opts);
      var onceShown = trialV ? trialV.price : once;
      /* WHAT THE SUBSCRIPTION SAVES AGAINST, "just like the PDP" (Sep 29).
         The PDP measures it against its trial box. V4.7's plans cost the
         same as its boxes bought once, so against the box itself nothing is
         saved and the green strap stayed down; against the trial it is the
         same sachets bought as 14-sachet boxes — 28 is two ($118 vs $99),
         56 is four ($236 vs $159). The count is the number in each format's
         name. `once` itself stays the box's list price for the first-order
         row. */
      var countIn = function (s) { var m = /(\d+)/.exec(s || ''); return m ? parseInt(m[1], 10) : 0; };
      var baseOnce = once;
      if (trialV && countIn(trialFmt) > 0 && countIn(variant.opts[0]) > 0) {
        baseOnce = Math.round(trialV.price * countIn(variant.opts[0]) / countIn(trialFmt));
      }
      var sub;
      if (hasOffers) {
        /* The subscription price is the whole period, not one unit: a
           90-day supply is five boxes billed together. */
        sub = offerBilled(currentOffer(), variant);
      } else {
        sub = pid && variant.plans && variant.plans[pid] !== undefined
          ? variant.plans[pid]
          : null;
      }

      /* A DELIVERY IS BILLED; A MONTH IS QUOTED.
       *
       * The subscription cards say "/mo", but a 90-day supply bills $297
       * once for three months. So everything that carries that unit shows
       * the period divided by the months it covers ($99), while the
       * frequency row keeps saying what actually leaves the card, and every
       * saving below still compares whole periods. */
      var months = (hasOffers && currentOffer()) ? (currentOffer().months || 1) : 1;
      var subMonthly = (sub === null || sub === undefined) ? sub : Math.round(sub / months);
      /* The picker's units on each delivery (qtySub). The cards keep quoting
         one; everything that says what the order bills or brings is times it. */
      var subMult = (qtySub && isSub) ? buyQty : 1;
      var subAll = (sub === null || sub === undefined) ? sub : sub * subMult;

      setText(els.oncePrice, money(onceShown, data.moneyFormat));
      setText(els.subPrice, sub === null ? '—' : money(subMonthly, data.moneyFormat));
      /* The sale's struck-through prices (A PAGE'S SALE, above). */
      var showWas = function (el, cents) {
        if (!el) return;
        var on = onSale && cents !== null && cents !== undefined && cents > 0;
        el.hidden = !on;
        el.textContent = on ? money(fullOf(cents), data.moneyFormat) : '';
      };
      showWas(els.onceWas, onceShown);
      showWas(els.subWas, sub === null ? null : subMonthly);
      /* The cadence the row is on: the supply product's plan with offers, the
       * product's own without (TwoStrong). A weekly one carries no "/mo",
       * as unitForOffer says. */
      var cadId = hasOffers ? chosenPlanId : pid;
      subUnit = hasOffers ? unitForOffer(currentOffer(), variant) : (planEvery(pid) ? '' : baseUnit);
      if (subUnitEl) subUnitEl.textContent = subUnit;
      // The frequency row's heading already names the plan (or the select
      // does), so the sub-line carries what it costs, not the name again.
      if (els.freqNote) {
        /* "$297 Billed Every 90 Days" — the one line on the page that says
           what actually leaves the card, since every other figure here is
           quoted per month. The wording is a setting so a shop that bills
           differently can say so. */
        var noteTpl = '{price} Billed Every {days} Days';
        /* A plan named in weeks is billed in weeks (2026-10-01, on request):
         * "Billed Every 28 Days" under "Ships every 4 weeks" asked the reader
         * to do arithmetic to see they were the same cycle. */
        var cadWord = planEvery(cadId);
        /* THE CADENCE, NOT THE SUPPLY (2026-10-01). The offer's own days are
         * how much product a delivery holds; once the frequency row became
         * the cadence, that stopped being how often the card is charged. A
         * plan named "Ships every 6 weeks" bills every 42 days, and every
         * option said 30 until this read the chosen plan's name. */
        var noteDays = (hasOffers && currentOffer() && currentOffer().days) || months * 30;
        var cadence = planDays(chosenPlanId);
        if (hasOffers && cadence) noteDays = cadence;
        /* ONE LINE FOR A WEEKLY CADENCE (2026-10-01, on request):
         * "Ships every 8 weeks – $159". The title and the price are the whole
         * statement -- ships and bills on the same cycle -- so the second
         * line only repeated the cadence in other words. Plans named in days
         * (OneFit V4.6) keep their two lines. A product's own weekly plans
         * read the same way (2026-10-20, TwoStrong's 4 / 6 / 8 weeks, which
         * have no supply offers): without this they said "Billed Every 30
         * Days" under "Ships every 6 weeks". */
        if (cadWord && sub !== null && sub !== undefined) {
          if (els.freqTitle) els.freqTitle.textContent = (chosenPlan || ('Ships every ' + cadWord)) +
            (data.freqPrice === false ? '' : ' \u2013 ' + money(subAll, data.moneyFormat));
          els.freqNote.textContent = '';
          els.freqNote.hidden = true;
          /* One line now, so the panel drops the height it kept for two. */
          if (els.freq) els.freq.classList.add('is-one-line');
        } else {
          els.freqNote.hidden = false;
          if (els.freq) els.freq.classList.remove('is-one-line');
          els.freqNote.textContent = sub === null || sub === undefined
            ? ''
            : noteTpl.replace('{price}', money(subAll, data.moneyFormat))
                     .replace('{days}', String(noteDays));
        }
      }
      // The banner copy holds a {saving} token rather than a typed number,
      // because the saving differs per variant — and it is pulled entirely
      // when there is nothing to save, rather than promising "Save " with a
      // hole where the number goes. OneLife's plan is currently at list
      // price, so this is not hypothetical.
      /* Like for like, as the saving pill below: a 90-day offer bills
         several boxes at once, so it is set against that many boxes at the
         one-time price. Against one box every multi-box offer read as a
         loss and the strap vanished on the best-value plan; against the
         right count OneLife's jar (sub = one-off) still saves nothing and
         the strap stays down. The hiding itself is finished in v88.css —
         .v88-plan__banner is display: flex, which beats the UA's [hidden]. */
      var offerQty = (hasOffers && currentOffer()) ? (currentOffer().qty || 1) : 1;

      /* "WHAT DO I GET ON MY FIRST ORDER?" DESCRIBES THE CHOSEN ORDER.
       *
       * Three things move with the supply. The count of the product
       * itself: three jars on a 90-day, one on a 30, where it had been
       * typed as three whatever the buy box was on. Its price, where the
       * row asks for it — the one-time cost of that many units. And
       * whether a row is there at all: the gifts are granted on the
       * 90-day bundles ONLY (see CLAUDE.md — onefit_90d, onelife_90d and
       * comprehensive_90d), so a 30 or 60-day order gets none, and
       * listing them beside those tiers promised what the cart would not
       * add. */
      root.querySelectorAll('[data-v88-gift-qty]').forEach(function (el) {
        el.textContent = (offerQty * subMult) + 'x';
      });
      /* One order earns each gift once, whatever the picker says
         (2026-10-15, Renz: "1 lang"; fon-cart-drawer-v2.js capGifts), so a
         gift row keeps its typed count and does not follow subMult. */
      root.querySelectorAll('[data-v88-gift-each]').forEach(function (el) {
        var n = parseInt(el.getAttribute('data-v88-gift-each'), 10);
        if (n > 0) el.textContent = n + 'x';
      });
      root.querySelectorAll('[data-v88-gift-price]').forEach(function (el) {
        /* PRICED OFF THE TRIAL (2026-10-01, on request). The row says what
         * the product in it is worth, and on OneFit that is measured against
         * the trial box, since a supply box bought once costs what the plan
         * does and would make the list read as worth nothing. Scaled to the
         * chosen format, as the green strap is (2026-10-05, Emily: OneFit's
         * row too) -- one trial box's $59 beside a 56-serve pouch was never
         * above the plan, so the strike never showed. Pages with no trial
         * format keep the variant's own price. */
        var rrp = baseOnce * offerQty * subMult;
        /* RRP STRUCK OVER THE SUBSCRIPTION PRICE (2026-10-05, Emily). The
         * panel sits in the subscription card, so the row says what the
         * delivery bills -- the whole period, as the frequency row does, to
         * match the "3x" beside it -- under the RRP it saves on. A plan
         * that saves nothing keeps the one price: a figure struck against
         * itself is no saving. */
        var saves = subAll !== null && subAll !== undefined && subAll > 0 && subAll < rrp;
        el.textContent = money(saves ? subAll : rrp, data.moneyFormat);
        var was = el.parentNode && el.parentNode.querySelector('[data-v88-gift-was]');
        if (was) {
          was.textContent = saves ? money(rrp, data.moneyFormat) : ' ';
          was.classList.toggle('is-blank', !saves);
        }
      });
      root.querySelectorAll('[data-v88-gift-min]').forEach(function (el) {
        var min = parseInt(el.getAttribute('data-v88-gift-min'), 10) || 1;
        el.hidden = offerQty < min;
      });
      /* A row with a second look wears it while its format value is the
         chosen one — the pouch for Pouches, the sachet box otherwise. */
      var chosenValues = [];
      els.optInputs.forEach(function (input) { if (input.checked) chosenValues.push(input.value); });
      /* Is this look's value the chosen one? Exactly, or as a pouch: the
         looks are named "Pouches" (V4.6's value) and V4.7's pouch is
         "56-serve pouch", so any chosen pouch answers to a pouch look
         (Sep 29) — the same rule the format pictures use in the Liquid. */
      var isPouch = function (s) { return /pouch/i.test(s || ''); };
      var picked = function (v) {
        return chosenValues.indexOf(v) > -1
          || (isPouch(v) && chosenValues.some(isPouch));
      };
      /* The gallery's first shot, and its thumbnail, follow the same
         choice: a look named for a chosen value shows, and the unnamed
         one ("") shows when none of the named ones is chosen. */
      var looks = root.querySelectorAll('[data-v88-fmt-look]');
      if (looks.length) {
        var named = false;
        looks.forEach(function (el) {
          var v = el.getAttribute('data-v88-fmt-look');
          if (v && picked(v)) named = true;
        });
        looks.forEach(function (el) {
          var v = el.getAttribute('data-v88-fmt-look');
          var show = v ? picked(v) : !named;
          el.hidden = !show;
          var img = el.tagName === 'IMG' ? el : el.querySelector('img');
          /* Eager for the lead shot; a later slide's look stays lazy
             (data-v88-fmt-lazy, the sachet/pouch swaps, Oct 19). */
          if (show && img && !el.hasAttribute('data-v88-fmt-lazy')) img.removeAttribute('loading');
        });
      }
      /* POUCH, POUCH ONLY (2026-10-15, Akaash, FINAL feedback: "When someone
         selects the pouch they should only see pouch images"). The gallery
         marks its sachet slides, and their thumbnails, data-v88-fmt-slide=
         "sachet" (a "pouch" one would be the reverse). They leave both rails
         while a pouch is chosen and return in their places otherwise. Taken
         out of the DOM, not hidden: Swiper pairs thumbnail n with slide n, and
         a hidden slide keeps its index but loses its place, so the rails
         would drift apart. Each rail's full list is kept on its wrapper
         (__v88Kids), which v88-gallery.js reads to pair the previews. */
      if (root.__fmtRails === undefined) {
        root.__fmtRails = [];
        root.querySelectorAll('.v88-pdp__hero .swiper-wrapper').forEach(function (w) {
          var kids = Array.prototype.slice.call(w.children);
          if (kids.some(function (k) { return k.hasAttribute('data-v88-fmt-slide'); })) {
            w.__v88Kids = kids;
            root.__fmtRails.push(w);
          }
        });
      }
      var pouchOn = chosenValues.some(isPouch);
      if (root.__fmtRails.length && root.__fmtPouch !== pouchOn) {
        var firstRun = root.__fmtPouch === undefined;
        root.__fmtPouch = pouchOn;
        root.__fmtRails.forEach(function (w) {
          w.__v88Kids.forEach(function (k) {
            var kind = k.getAttribute('data-v88-fmt-slide');
            if (!kind || (kind === 'pouch') === pouchOn) {
              k.hidden = false;
              w.appendChild(k);
            } else if (k.parentNode) {
              k.parentNode.removeChild(k);
            }
          });
        });
        /* Started already (v88-gallery.js may have run first): Swiper re-reads
           its slides, and a switch opens on the new format's first shot. */
        root.querySelectorAll('.v88-pdp__hero .swiper').forEach(function (el) {
          if (!el.swiper) return;
          el.swiper.update();
          if (!firstRun) el.swiper.slideTo(0, 0);
        });
      }
      /* THE FIRST SHOT FOLLOWS THE OFFER (2026-10-14, Emily, OneLife: "I've
         created a version for single and duo subscription, so it can change
         when user toggles"). The hero carries data-v88-offer-shots, "offer key
         | image URL" pairs from the offer_shots setting, already in the
         visitor's currency. While subscribing on a listed offer (For Two), the
         gallery's first image and its thumbnail show that offer's shot; on
         any other choice they return to what the page drew. */
      var shotHero = root.querySelector('[data-v88-offer-shots]');
      if (shotHero) {
        if (!shotHero.__shots) {
          var map = {};
          shotHero.getAttribute('data-v88-offer-shots').split(';;').forEach(function (pair) {
            var bits = pair.split('|');
            if (bits.length === 2 && bits[0].trim() && bits[1].trim()) map[bits[0].trim()] = bits[1].trim();
          });
          var mainImg = shotHero.querySelector('.main-gallery .swiper-slide img');
          var thumbImg = shotHero.querySelector('.swiper-main-gallery-thumbnails .swiper-slide img');
          shotHero.__shots = { map: map, main: mainImg, thumb: thumbImg,
            mainSrc: mainImg && mainImg.getAttribute('src'), thumbSrc: thumbImg && thumbImg.getAttribute('src') };
        }
        var sh = shotHero.__shots;
        var shot = isSub && chosenOffer ? sh.map[chosenOffer] : null;
        var withW = function (u, w) { return u + (u.indexOf('?') < 0 ? '?' : '&') + 'width=' + w; };
        if (sh.main) {
          var wantMain = shot ? withW(shot, 1500) : sh.mainSrc;
          if (wantMain && sh.main.getAttribute('src') !== wantMain) sh.main.setAttribute('src', wantMain);
        }
        /* The thumbnail is read now, not at first sight: v88-gallery.js may
           repoint it to the square frame after this first ran. */
        if (sh.thumb) {
          if (!shot) { if (sh.thumbShot && sh.thumb.getAttribute('src') === sh.thumbShot) sh.thumb.setAttribute('src', sh.thumbBase); }
          else {
            if (sh.thumb.getAttribute('src') !== withW(shot, 200)) sh.thumbBase = sh.thumb.getAttribute('src');
            sh.thumbShot = withW(shot, 200);
            sh.thumb.setAttribute('src', sh.thumbShot);
          }
        }
      }
      root.querySelectorAll('[data-v88-gift-alt], [data-v88-gift-qty-min], [data-v88-gift-variant]').forEach(function (li) {
        var on = li.hasAttribute('data-v88-gift-alt') && picked(li.getAttribute('data-v88-gift-alt'));
        var img = li.querySelector('[data-v88-gift-img]');
        if (img) {
          /* A bigger supply can show a bigger picture (three jars on a
             90-day); that wins over the format's own look, which wins
             over the row's rest image. */
          var qtyMin = parseInt(li.getAttribute('data-v88-gift-qty-min'), 10);
          var src = (li.hasAttribute('data-v88-gift-variant') && variant && variant.image)
            || (qtyMin && offerQty >= qtyMin && img.getAttribute('data-v88-gift-qty-src'))
            || (on ? img.getAttribute('data-v88-gift-alt-src') : img.getAttribute('data-v88-gift-src'));
          if (src && img.getAttribute('src') !== src) img.setAttribute('src', src);
        }
        var label = li.querySelector('[data-v88-gift-label]');
        if (label) {
          var text = on ? label.getAttribute('data-v88-gift-alt-text') : label.getAttribute('data-v88-gift-text');
          if (text) label.textContent = text;
        }
      });
      /* What the subscription saves over the whole period — the strap's
         figure. The one-time card divides it below. */
      var periodSaving = sub === null || sub === undefined ? 0 : ((baseOnce * offerQty) - sub) * subMult;
      /* PER BOX, NOT PER PERIOD. The card sells ONE box at `once` (337),
         so a period saving beside it compares five boxes against a single
         purchase — $403 against a $140 card, which Akaash read as out of
         wack. 235:444 says "Save $88 On Each Order" against a one-box
         price, so the order this line means is the card's own. */
      var boxSaving = offerQty > 0 ? Math.round(periodSaving / (offerQty * subMult)) : periodSaving;
      root.querySelectorAll('[data-v88-miss-saving]').forEach(function (el) {
        el.textContent = boxSaving > 0 ? 'Save ' + money(boxSaving, data.moneyFormat) + ' on each order' : 'Save on each order';
      });
      if (els.banner && bannerTpl) {
        var saving = periodSaving;
        var tplNow = bannerTpl;
        if (annualMap) tplNow = tplNow.replace('[annual]', (isSub && chosenOffer && annualMap[chosenOffer]) || annualMap['*'] || '');
        els.banner.textContent = tplNow.replace('{saving}', money(saving, data.moneyFormat));
        var bannerRow = els.banner.closest('.v88-plan__banner') || els.banner;
        bannerRow.hidden = saving <= 0;
      }

      // The subscription card cannot be chosen for a variant with no plan.
      var subInput = els.subCard && els.subCard.querySelector('input[data-v88-mode]');
      if (subInput) {
        if (sub === undefined) sub = null;
        subInput.disabled = sub === null;
        els.subCard.classList.toggle('is-unavailable', sub === null);
        if (sub === null && isSub) {
          var onceInput = els.onceCard && els.onceCard.querySelector('input[data-v88-mode]');
          if (onceInput) { onceInput.checked = true; isSub = false; }
        }
      }

      var total = isSub && sub !== null ? subAll : onceShown * (maxQty > 0 ? buyQty : 1);
      /* The big figure wears the same "/mo" as the card, so it quotes the
         same monthly number; the savings below keep using the period. */
      setText(els.total, money(isSub && sub !== null ? subMonthly * subMult : total, data.moneyFormat));
      showWas(els.totalWas, isSub && sub !== null ? subMonthly * subMult : total);
      /* The total is a per-period figure on a subscription, so it says so --
         in the card's own unit, which the offer decides. The Liquid prints
         the template's, so V4.7's 4-week total read "/mo" under a card with
         none. */
      if (els.totalUnit) {
        els.totalUnit.textContent = subUnit;
        els.totalUnit.hidden = !(isSub && sub !== null && subUnit);
      }
      /* PRICE PER SERVE (per_serve, Oct 14: the GLP-1 4.7 lander, Akaash's
         "Price per serve needs to be updated"). The figure the big total
         shows, over the serves it buys: the format's own count ("56-serve
         pouch", "28-sachet box") times the units in it. Hidden when the
         format names no count. */
      if (els.perServe) {
        var psM = /(\d+)-(sachet|serve)/i.exec(String(variant && variant.opts ? variant.opts[0] : ''));
        var psUnits = isSub && sub !== null
          ? ((hasOffers && currentOffer()) ? (currentOffer().qty || 1) : 1) * subMult
          : (maxQty > 0 ? buyQty : 1);
        var psCount = psM ? Number(psM[1]) * psUnits : 0;
        var psPrice = isSub && sub !== null ? subAll : total;
        if (psCount > 0 && psPrice > 0) {
          els.perServe.textContent = money(Math.round(psPrice / psCount), data.moneyFormat) + ' per serve';
          els.perServe.hidden = false;
        } else {
          els.perServe.hidden = true;
        }
      }
      if (els.save) {
        /* Compare like for like. A 90-day supply is five boxes, so the thing
           it saves against is five boxes at the one-time price — not one.
           Comparing a $297 period to a $140 box made the best-value offer
           look like it saved nothing, which is the exact opposite of true. */
        var baseQty = ((hasOffers && isSub && currentOffer()) ? (currentOffer().qty || 1) : 1) * subMult;
        var saved = (baseOnce * baseQty) - total;
        /* A trial box is a smaller box, not a discount on the chosen one. */
        if (!isSub && trialV) saved = 0;
        /* On a sale the pill is the sale: what this whole order would have
           cost before it, less what it costs now. */
        if (onSale) saved = fullOf(total) - total;
        els.save.hidden = saved <= 0;
        setText(els.save, saved > 0 ? 'Save ' + money(saved, data.moneyFormat) : '');
      }

      if (!waitlist) {
        var buyV = (!isSub && trialV) ? trialV : variant;
        if (soon(buyV)) {
          els.cta.disabled = false;
          /* In its two halves, so the phone can stack them and keep the
             button beside the price (v88.css, .v88-pdp__cta-txt). */
          var notifyParts = String(data.notifyCta || 'Coming Soon | Notify Me').split('|');
          els.cta.innerHTML = notifyParts.length === 2
            ? '<span class="v88-pdp__cta-txt"><span>' + esc(notifyParts[0].trim()) + '</span>' +
              '<span class="v88-pdp__cta-sep"> | </span><span>' + esc(notifyParts[1].trim()) + '</span></span>'
            : esc(notifyParts.join('|'));
          setSoon(true);
        } else if (waiting(buyV)) {
          els.cta.disabled = true;
          els.cta.textContent = 'Coming Soon';
          setSoon(false, true);
        } else {
          els.cta.disabled = !buyV.available;
          els.cta.textContent = buyV.available ? ctaLabel : 'Sold Out';
          setSoon(false);
          /* "In Stock" under a Sold Out button contradicts it. */
          if (stockEl && !buyV.available) stockEl.hidden = true;
        }
      }

      /* THE PERK THAT COUNTS THE SUPPLY ("90 Sachets | Designed For Daily
         Use") follows the pick (Renz, Sep 29: "28 sachets, 56 serves";
         "pouches not sachets, it's serves — dynamic when days are
         selected"). A format with the number in its name (V4.7's "28-sachet
         box", "56-serve pouch") counts that, times the units delivered.
         Otherwise it is one a day: the days of the chosen delivery (V4.6,
         OneLife). Pouches count serves, sachets sachets; a product with
         neither keeps the typed word. */
      var planM = /(\d+)\s*Day/i.exec(els.freqTitle ? els.freqTitle.textContent : '');
      var units = (hasOffers && isSub && currentOffer()) ? (currentOffer().qty || 1) : 1;
      Array.prototype.forEach.call(servesEls, function (el) {
        var fmt = String(variant.opts[0] || '');
        /* A pouch line of its own (2026-10-15, "56 Serves | 8 Weeks of Daily
           Supplements Handled") while a pouch is chosen, the plain one else. */
        var pouchLine = el.getAttribute('data-v88-serves-pouch');
        var base = (pouchLine && /pouch/i.test(fmt)) ? pouchLine : el.getAttribute('data-v88-serves-base');
        var bm = /^(\d+)\s+(\S+)/.exec(base);
        if (!bm) { el.textContent = base; return; }
        var m = /(\d+)-(sachet|serve)/i.exec(fmt);
        var n, word;
        if (m) {
          n = Number(m[1]) * units;
          /* A line typed in Serves keeps the word on the sachet box too
             (2026-10-15, Akaash: "28 Serves | 4 Weeks of Supplements, Sorted"). */
          word = /sachet/i.test(m[2]) && !/^serves$/i.test(bm[2]) ? 'Sachets' : 'Serves';
        } else if (data.servesPerUnit > 0) {
          /* A unit with a fixed count -- a OneLife jar holds 30 whatever the
           * cadence (2026-10-01, on request) -- so the line follows the tier:
           * 30 Serves For One, 60 For Two. The cadence governs how often the
           * delivery lands, not how much is in it. */
          n = data.servesPerUnit * units;
          word = bm[2];
          /* "max-days" (plant-based, 2026-10-15): per unit, capped at the
           * delivery's days -- 20 every 30 days, 60 every 60, 90 every 90
           * though it ships five. Pouches say Serves, sachets Sachets. */
          if (data.servesCapDays) {
            if (planM) n = Math.min(n, Number(planM[1]));
            /* A line typed in Serves keeps the word on sachets too, as the
               4.7's does (2026-10-15: "20 Serves | One A Day For Best
               Results"). */
            word = /pouch/i.test(fmt) || /^serves$/i.test(bm[2]) ? 'Serves' : (/sachet/i.test(fmt) ? 'Sachets' : bm[2]);
          }
        } else {
          n = planM ? Number(planM[1]) : Number(bm[1]);
          word = /pouch/i.test(fmt) ? 'Serves' : (/sachet/i.test(fmt) ? 'Sachets' : bm[2]);
        }
        /* Times the picker's units, after any cap: the cap is one a day for
           one person, and two pouches a delivery are two people's. */
        n = n * subMult;
        el.textContent = base.replace(/^\d+\s+\S+/, n + ' ' + word);
      });
    }

    function fail(msg) {
      if (!els.error) return;
      els.error.textContent = msg;
      els.error.hidden = false;
    }

    els.cta.addEventListener('click', function () {
      if (waitlist) return;
      var variant = findVariant(chosenOptions());
      /* The trial buys the trial's box in the chosen flavour. */
      if (mode() !== 'sub' && trialFmt) variant = trialVariant(chosenOptions());
      /* Coming soon asks for the form, of whichever variant render() judged
         (the chosen one when the flavour has no trial box). */
      var askV = variant || findVariant(chosenOptions());
      if (soon(askV)) { openNotify(askV); return; }
      if (!variant || !variant.available) return;
      if (els.error) els.error.hidden = true;

      var payload = { id: variant.id, quantity: 1 };
      var subscribing = mode() === 'sub';

      if (subscribing && hasOffers) {
        /* A supply offer buys a DIFFERENT product at a quantity, so the
           whole line is replaced rather than a selling_plan being bolted
           onto the base variant.

           The properties mirror the live PDP's checkout30d/60d/90d exactly.
           They are not decoration: the cart drawer folds a bundle by
           _bundle_group_id and reads _BUNDLE against its GIFT_RULES to work
           out what the order earns. Get them wrong and the bundle shows as
           loose lines with no gifts. */
        var offer = currentOffer();
        var row = offer && offerRow(offer, variant);
        if (!row) { fail('That supply is not available in this flavour.'); return; }

        payload = {
          id: row.id,
          quantity: (offer.qty || 1) * (qtySub ? buyQty : 1),
          properties: {
            _BUNDLE: offer.bundle,
            _bundle_group_id: offer.bundle + '_' + row.id,
            _bundle_group_product: true,
            _bundle_group_product_row: 1,
            _offer: offer.bundle + '_' + row.id,
            _offer_product_type: 'primary'
          }
        };
        /* The cadence the reader chose, not whichever allocation came first
         * (2026-10-01): the frequency row is the ship cycle now, so picking
         * "every 8 weeks" and getting a 4-week plan in the cart would be the
         * page lying about what it sold. */
        var addPlan = (chosenPlanId && row.plans && row.plans[chosenPlanId] !== undefined)
          ? chosenPlanId
          : row.planId;
        if (addPlan) payload.selling_plan = Number(addPlan);
      } else if (subscribing) {
        var pid = syncFreq(variant) || Object.keys(variant.plans || {})[0];
        if (pid && variant.plans && variant.plans[pid] !== undefined) {
          payload.selling_plan = Number(pid);
        }
        if (qtySub) payload.quantity = buyQty;
      }

      if (!subscribing && maxQty > 0) {
        payload.quantity = buyQty;
        if (!qtySoft) payload.properties = { _max_qty: String(maxQty) };
      }

      els.cta.disabled = true;
      els.cta.textContent = 'Adding…';

      /* The cap counts what is already in the cart, in any line of this variant. */
      var room = (!subscribing && maxQty > 0 && !qtySoft)
        ? fetch('/cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
          .then(function (r) { return r.json(); })
          .then(function (cart) {
            var have = 0;
            (cart.items || []).forEach(function (it) { if (Number(it.variant_id) === Number(variant.id)) have += it.quantity; });
            if (have + payload.quantity <= maxQty) return;
            var left = Math.max(0, maxQty - have);
            throw new Error(left
              ? 'Max ' + maxQty + ' per person. You have ' + have + ' in your cart, so you can add ' + left + ' more.'
              : 'Max ' + maxQty + ' per person, and you already have ' + have + ' in your cart.');
          })
        : Promise.resolve();

      room
        .then(function () {
          return fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify(payload)
          });
        })
        .then(function (r) { return r.json().then(function (b) { return { ok: r.ok, body: b }; }); })
        .then(function (res) {
          if (!res.ok) throw new Error(res.body && res.body.description ? res.body.description : 'Add to cart failed');
          els.cta.disabled = false;
          els.cta.textContent = ctaLabel;
          // Hand off to the theme's drawer when it is there; /cart otherwise.
          if (window.FonCartDrawer && typeof window.FonCartDrawer.openCartDrawer === 'function') {
            window.FonCartDrawer.openCartDrawer();
            if (typeof window.FonCartDrawer.updateCartDrawer === 'function') window.FonCartDrawer.updateCartDrawer();
          } else {
            window.location.href = '/cart';
          }
        })
        .catch(function (err) {
          els.cta.disabled = false;
          els.cta.textContent = ctaLabel;
          fail(err.message || 'Sorry, that could not be added to your cart. Please try again.');
        });
    });

    root.addEventListener('change', function (e) {
      if (e.target.matches('input[data-v88-opt], input[data-v88-mode]')) render();
      /* Choosing a flavour shuts the list again, the way the unicorn
       * card's does — the row goes back to showing what was picked. */
      if (e.target.matches('input[data-v88-opt]')) {
        var drop = e.target.closest ? e.target.closest('[data-v88-drop]') : null;
        if (drop) shut(drop);
      }
    });

    /* THE FLAVOUR LIST OPENS ON THE BUTTON, not on the label. Delegated so
     * it survives a render, and mirrored onto aria-expanded so the control
     * announces its own state. */
    function shut(drop) {
      drop.classList.remove('is-open');
      var btn = drop.querySelector('[data-v88-drop-btn]');
      if (btn) btn.setAttribute('aria-expanded', 'false');
    }
    /* Tells the stylesheet the button works, so the list may close. Until
     * this runs the list stays open and the button is hidden — a shut
     * drawer with no handler would be a flavour picker nobody can open. */
    Array.prototype.forEach.call(root.querySelectorAll('[data-v88-drop]'), function (drop) {
      drop.classList.add('v88-drop-ready');
    });

    root.addEventListener('click', function (e) {
      var btn = e.target.closest ? e.target.closest('[data-v88-drop-btn]') : null;
      if (!btn) return;
      var drop = btn.closest('[data-v88-drop]');
      if (!drop) return;
      var open = !drop.classList.contains('is-open');
      drop.classList.toggle('is-open', open);
      btn.setAttribute('aria-expanded', open ? 'true' : 'false');
    });

    /* Picking an option from the panel. Delegated, because the list is
     * rewritten on every render. */
    root.addEventListener('click', function (e) {
      var opt = e.target.closest ? e.target.closest('[data-v88-freq-opt]') : null;
      if (!opt) return;
      var val = opt.getAttribute('data-v88-freq-opt');
      /* The row is the cadence on both paths now: with offers it holds the
       * supply product's plan ids, without them the product's own. The
       * quantity moved to the chips below. */
      if (opt.hasAttribute('data-v88-freq-offer')) {
        chosenOffer = val;
      } else {
        chosenPlan = opt.querySelector('span') ? opt.querySelector('span').textContent : val;
        if (hasOffers) chosenPlanId = val;
      }
      if (els.freq) els.freq.open = false;
      render();
    });

    /* For One / For Two. Same delegation, same reason. */
    root.addEventListener('click', function (e) {
      var chip = e.target.closest ? e.target.closest('[data-v88-subqty-opt]') : null;
      if (!chip) return;
      chosenOffer = chip.getAttribute('data-v88-subqty-opt');
      render();
    });

    /* THE PRICE-PER-DAY BUTTON PICKS ITS OWN OFFER (2026-10-14, Emily: "when
     * this is selected at bottom of PDP, that it scrolls to that cheapest
     * item (to avoid confusion)"). sections/v88-worth.liquid marks its button
     * with the offer and format its per day is priced on; the click turns on
     * the subscription, chooses them, and lets the link scroll here. */
    document.addEventListener('click', function (e) {
      var a = e.target.closest ? e.target.closest('[data-v88-pick]') : null;
      if (!a) return;
      var subInput = root.querySelector('input[data-v88-mode="sub"]');
      if (subInput && !subInput.disabled) subInput.checked = true;
      var fmt = (a.getAttribute('data-v88-pick-fmt') || '').toLowerCase();
      if (fmt) {
        Array.prototype.forEach.call(els.optInputs, function (input) {
          if (input.getAttribute('data-v88-opt') === '0' && input.value.toLowerCase() === fmt) input.checked = true;
        });
      }
      var offer = a.getAttribute('data-v88-pick-offer');
      if (offer) chosenOffer = offer;
      render();
    });

    /* ?flavour=SKU — the site banner's link (live PR #97). The live PDP
     * reads it in onefit-all-b-shorter.js; this is the same pick here: the
     * variant with that SKU chosen, format and flavour both, and the
     * purchase type left on the page's default. */
    (function () {
      var sku = new URLSearchParams(window.location.search).get('flavour');
      if (!sku || !/^[A-Z0-9.-]+$/i.test(sku)) return;
      var map = data.skuByVariant || {};
      var id = Object.keys(map).filter(function (k) { return map[k] === sku; })[0];
      var v = null;
      for (var i = 0; id && i < data.variants.length; i++) if (String(data.variants[i].id) === id) v = data.variants[i];
      if (!v) return;
      Array.prototype.forEach.call(els.optInputs, function (input) {
        var pos = Number(input.getAttribute('data-v88-opt'));
        if (v.opts[pos] === input.value) input.checked = true;
      });
    })();

    render();
  }

  function boot() {
    Array.prototype.forEach.call(document.querySelectorAll('.v88-pdp'), init);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();

/* FLASH DEAL ROWS (sections/v88-buybox.liquid, batch_*; Oct 5): the
 * visitor's dates for {today} / {tomorrow}, and the {timer} counting to
 * their midnight. */
(function () {
  function day(n) {
    var d = new Date(); d.setDate(d.getDate() + n);
    try { return d.toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' }); }
    catch (e) { return d.toDateString().slice(0, 10); }
  }
  function pad(n) { return n < 10 ? '0' + n : String(n); }
  function init() {
    document.querySelectorAll('[data-v88-batch]').forEach(function (box) {
      if (box.hasAttribute('data-v88-batch-on')) return;
      box.setAttribute('data-v88-batch-on', '');
      box.querySelectorAll('[data-v88-datelabel]').forEach(function (el) {
        var t = el.getAttribute('data-v88-datelabel');
        if (t.indexOf('{today}') > -1 || t.indexOf('{tomorrow}') > -1) el.textContent = t.replace('{today}', day(0)).replace('{tomorrow}', day(1));
      });
      /* The heading's {timer}: the text either side, the clock between. */
      var head = box.querySelector('[data-v88-batch-head]');
      var tpl = head ? head.getAttribute('data-v88-batch-head') : '';
      if (head && tpl.indexOf('{timer}') > -1) {
        var parts = tpl.split('{timer}');
        head.textContent = parts[0];
        var span = document.createElement('span');
        span.className = 'v88-batch__timer';
        span.setAttribute('data-v88-timer', '');
        head.appendChild(span);
        head.appendChild(document.createTextNode(parts.slice(1).join('')));
      }
      var tm = box.querySelector('[data-v88-timer]');
      if (!tm) return;
      /* batch_mins (2026-10-06, the VIP lander: "make the timer 15 mins"):
       * a countdown of that many minutes from the visitor's first view in
       * this tab session, starting over when it runs out. 0: to midnight. */
      var mins = parseInt(box.getAttribute('data-v88-batch-mins'), 10) || 0;
      /* batch_until (2026-10-06, the VIP lander: "VIP ACCESS ENDS IN
       * [countdown to Friday]"): a fixed end, the same moment for everyone;
       * days lead once it is more than one away. Wins over mins. */
      var until = Date.parse(box.getAttribute('data-v88-batch-until') || '');
      var key = 'v88-batch-end-' + mins;
      var runEnd = 0;
      function minsEnd() {
        var now = Date.now();
        if (!runEnd) { try { runEnd = Number(sessionStorage.getItem(key)) || 0; } catch (e) {} }
        if (runEnd <= now) {
          runEnd = now + mins * 60000;
          try { sessionStorage.setItem(key, String(runEnd)); } catch (e) {}
        }
        return runEnd;
      }
      function tick() {
        var end;
        if (!isNaN(until)) end = until;
        else if (mins > 0) end = minsEnd();
        else { end = new Date(); end.setHours(24, 0, 0, 0); }
        var s = Math.max(0, Math.floor((end - new Date()) / 1000));
        if (!isNaN(until) && s >= 86400) {
          var d = Math.floor(s / 86400);
          s = s % 86400;
          tm.textContent = d + 'd ' + pad(Math.floor(s / 3600)) + ':' + pad(Math.floor(s % 3600 / 60)) + ':' + pad(s % 60);
          return;
        }
        tm.textContent = (mins > 0 && isNaN(until) && s < 3600)
          ? pad(Math.floor(s / 60)) + ':' + pad(s % 60)
          : pad(Math.floor(s / 3600)) + ':' + pad(Math.floor(s % 3600 / 60)) + ':' + pad(s % 60);
      }
      tick(); setInterval(tick, 1000);
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
})();
